import React from "react";
import isEmpty from "lodash/isEmpty";
import { useFormState } from "react-final-form";
import Flx from "../../../components/layout/Flx";
import PublicFormGrid from "../../../components/publicForm/PublicFormGrid";
import PublicFormSection from "../../../components/publicForm/PublicFormSection";
import DependentGroup from "../../../components/publicForm/DependentGroup";
import RffBooleanCheckbox from "../../../components/finalForm/inputs/RffBooleanCheckbox";
import RffBooleanToggleField from "../../../components/finalForm/inputs/RffBooleanToggleField";
import RffDollarField from "../../../components/finalForm/inputs/RffDollarField";
import RffCheckboxArrayField from "../../../components/finalForm/inputs/RffCheckboxArrayField";
import RffSelectToggleField from "../../../components/finalForm/inputs/RffSelectToggleField";
import RffCheckboxGroup from "../../../components/finalForm/shared/RffCheckboxGroup";
import RffConditional from "../../../components/finalForm/shared/RffConditional";
import ExperienceSection from "../components/ExperienceSection";
import {
  FICO_OPTIONS,
  HOUSING_EVENT_OPTIONS,
  PRIMARY_RESIDENCE_OWNERSHIP_OPTIONS,
  TIME_SINCE_HOUSING_EVENT_OPTIONS,
} from "../applicationFormShared";

/** Step 2 — liquidity, credit history, housing, and prior investing experience. */
const StepBackgroundExperience = () => {
  const { values } = useFormState({ subscription: { values: true } });

  return (
    <Flx column>
      <PublicFormSection
        title="Liquidity &amp; Credit"
        description="Estimates are fine — we'll verify these later in underwriting."
      >
        <PublicFormGrid>
          <RffDollarField
            name="cashLiquidity"
            label="Please detail your total cash liquidity in personal and/or entity bank accounts"
          />
          <RffDollarField
            name="otherLiquidity"
            label="Please detail total value of other liquidity sources"
            helperText="Including money market/securities account, cash value of life insurance policies, IRA or 401k accounts"
          />
          <RffSelectToggleField
            name="estimatedFICO"
            label="What is your estimated FICO range?"
            options={FICO_OPTIONS}
          />
        </PublicFormGrid>
      </PublicFormSection>

      <PublicFormSection title="Your Primary Residence">
        <PublicFormGrid>
          <RffSelectToggleField
            required
            name="primaryResidenceOwnership"
            label="Do you own or rent your current primary residence?"
            options={PRIMARY_RESIDENCE_OWNERSHIP_OPTIONS}
          />
          {/* Owning answers this implicitly, so it's only asked of renters.
              Together these derive `firstTimeHomebuyer` at submit time. */}
          <RffConditional field="primaryResidenceOwnership" value="Rent">
            <DependentGroup>
              <RffBooleanToggleField
                suppressGrid
                name="hasEverOwnedPrimaryResidence"
                label="Have you ever owned a primary residence?"
              />
            </DependentGroup>
          </RffConditional>
        </PublicFormGrid>
      </PublicFormSection>

      <PublicFormSection title="Credit &amp; Legal History">
        <PublicFormGrid>
          {/* RffCheckboxGroup doesn't go through RffInputWrapper, so it needs to
              claim the full row itself. */}
          <RffCheckboxGroup label="Have you ever:" sx={{ gridColumn: "1 / -1" }}>
            <RffBooleanCheckbox
              name="application_felony_conviction"
              label="Been convicted of a felony"
            />
            <RffBooleanCheckbox
              name="application_financial_fraud_misdemeanor"
              label="Been convicted of any financial or fraud related crimes including misdemeanors"
            />
            <RffBooleanCheckbox
              name="bankruptcy_flag"
              label="Filed for bankruptcy in the last 5 years"
            />
          </RffCheckboxGroup>

          <RffBooleanToggleField
            name="open_liens_judgments"
            label="Do you have any open charge offs, federal or state tax liens, pending litigation or financial judgments?"
          />
          <RffConditional field="open_liens_judgments" value={true}>
            <DependentGroup label="Select All That Apply">
              <RffBooleanCheckbox name="charge_offs" label="Charge Offs" />
              <RffBooleanCheckbox name="tax_liens" label="Tax Liens" />
              <RffBooleanCheckbox
                name="financial_judgements"
                label="Financial Judgements"
              />
              <RffBooleanCheckbox
                name="pending_litigation"
                label="Pending Litigation"
              />
            </DependentGroup>
          </RffConditional>
        </PublicFormGrid>
      </PublicFormSection>

      <PublicFormSection
        title="Housing Events"
        description="Leave blank if none of these apply to you."
      >
        <PublicFormGrid>
          {/* A checklist rather than a multi-select: these options are the
              answer to a long question, and burying them behind a dropdown made
              the applicant open a menu to find out what was being asked. Same
              stored array as the multi-select it replaced. */}
          <RffCheckboxArrayField
            name="housing_events"
            label="In the last three years, have you entered into a loan forbearance or modification program, had a property owned as an individual or through an entity under your control subject to lis pendens or foreclosure, or had any mortgage lates?"
            options={HOUSING_EVENT_OPTIONS}
            helperText="Select all that apply"
            columns={2}
          />
          {isEmpty(values?.housing_events) ? null : (
            <RffSelectToggleField
              name="time_since_housing_event"
              label="Time Since Last Housing Event"
              options={TIME_SINCE_HOUSING_EVENT_OPTIONS}
            />
          )}
        </PublicFormGrid>
      </PublicFormSection>

      <PublicFormSection title="Experience">
        <ExperienceSection />
      </PublicFormSection>
    </Flx>
  );
};

export default StepBackgroundExperience;
